import { buildApiUrl } from '@/utils/apiUrlConfig';

/**
 * Client API centralisé qui gère automatiquement l'authentification
 * et les erreurs pour toutes les requêtes
 */
export const apiClient = {
  /**
   * Vérifie si un token JWT valide est présent et extrait l'ID utilisateur
   * @returns userId ou null si pas de token ou token invalide
   */
  getUserId(): number | null {
    // Méthode 1: Essayer de récupérer l'ID utilisateur du userData stocké
    const userData = sessionStorage.getItem('userData');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.userId) {
          console.log('ID utilisateur récupéré depuis userData:', user.userId);
          return typeof user.userId === 'string' ? parseInt(user.userId, 10) : user.userId;
        }
      } catch (e) {
        console.error('Erreur lors du parsing des données utilisateur:', e);
      }
    }
    
    // Méthode 2: Récupérer directement l'ID utilisateur stocké
    const userId = sessionStorage.getItem('currentUserID');
    if (userId) {
      console.log('ID utilisateur récupéré depuis currentUserID:', userId);
      return parseInt(userId, 10);
    }
    
    // Méthode 3: Essayer de récupérer depuis le token Firebase
    const currentUid = sessionStorage.getItem('currentUserUID');
    if (!currentUid) {
      console.log('Aucun UID utilisateur trouvé dans sessionStorage');
      return null;
    }

    // Récupère le token à partir de l'UID
    const tokenKey = `firebaseAuthToken_${currentUid}`;
    const token = sessionStorage.getItem(tokenKey);
    
    if (!token) {
      console.log('Aucun token trouvé dans sessionStorage pour la clé', tokenKey);
      return null;
    }

    try {
      // Décoder le token JWT (format: header.payload.signature)
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        console.error('Format de token JWT invalide');
        return null;
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      // Priorité à userId, puis id, puis sub si présent
      const userId = payload.userId || payload.id || payload.sub;
      
      // Vérifier que l'ID est présent
      if (!userId) {
        console.error('Aucun ID utilisateur trouvé dans le token:', payload);
        return null;
      }
      
      if (typeof userId === 'string' && !isNaN(parseInt(userId, 10))) {
        console.log('ID utilisateur numérique extrait du token:', userId);
        return parseInt(userId, 10);
      } else if (typeof userId === 'number') {
        console.log('ID utilisateur numérique extrait du token:', userId);
        return userId;
      } else {
        console.log('ID utilisateur extrait du token est un UID Firebase:', userId);
        return null; // C'est un UID Firebase, pas un ID numérique
      }
    } catch (error) {
      console.error('Erreur lors du décodage du token JWT:', error);
      return null;
    }
  },

  /**
   * Méthode fetch améliorée qui ajoute automatiquement l'authentification
   */
  async fetch(url: string, options: RequestInit = {}): Promise<any> {
    // Récupère l'UID actuel depuis sessionStorage
    const currentUid = sessionStorage.getItem('currentUserUID');
    let token = null;
    
    if (currentUid) {
      const tokenKey = `firebaseAuthToken_${currentUid}`;
      token = sessionStorage.getItem(tokenKey);
    }
    
    // Utiliser la fonction buildApiUrl pour construire l'URL complète
    const fullUrl = buildApiUrl(url);
    
    // Fusion des en-têtes par défaut avec ceux fournis
    const headers = {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) || {})
    } as Record<string, string>;

    // Ajouter le token d'authentification s'il est disponible
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Configurer les options de la requête
    const fetchOptions: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(fullUrl, fetchOptions);

      // Vérifier si la réponse est OK (status 200-299)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || 'Erreur lors de la requête API');
        (error as any).status = response.status;
        (error as any).data = errorData;
        throw error;
      }

      // Pour les réponses sans contenu (204)
      if (response.status === 204) {
        return null;
      }

      // Analyser la réponse JSON
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Erreur API:', error);
      throw error;
    }
  },

  /**
   * Effectue une requête GET
   * @param url L'URL relative de l'API
   * @param options Options supplémentaires pour la requête
   * @returns Données de la réponse
   */
  get(url: string, options: { params?: Record<string, any> } & Omit<RequestInit, 'method'> = {}): Promise<any> {
    const { params, ...fetchOptions } = options;
    
    // Ajouter les query params à l'URL si présents
    if (params && Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, value.toString());
        }
      });
      
      const queryString = queryParams.toString();
      url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
    }
    
    return this.fetch(url, { ...fetchOptions, method: 'GET' });
  },

  /**
   * Effectue une requête POST
   * @param url L'URL relative de l'API
   * @param data Les données à envoyer (seront converties en JSON)
   * @param options Options supplémentaires pour la requête
   * @returns Données de la réponse
   */
  post(url: string, data?: any, options: RequestInit = {}): Promise<any> {
    return this.fetch(url, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * Effectue une requête PUT
   * @param url L'URL relative de l'API
   * @param data Les données à envoyer (seront converties en JSON)
   * @param options Options supplémentaires pour la requête
   * @returns Données de la réponse
   */
  put(url: string, data?: any, options: RequestInit = {}): Promise<any> {
    return this.fetch(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * Effectue une requête PATCH
   * @param url L'URL relative de l'API
   * @param data Les données à envoyer (seront converties en JSON)
   * @param options Options supplémentaires pour la requête
   * @returns Données de la réponse
   */
  patch(url: string, data?: any, options: RequestInit = {}): Promise<any> {
    return this.fetch(url, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * Effectue une requête DELETE
   * @param url L'URL relative de l'API
   * @param options Options supplémentaires pour la requête
   * @returns Données de la réponse
   */
  delete(url: string, options: RequestInit = {}): Promise<any> {
    return this.fetch(url, {
      ...options,
      method: 'DELETE',
    });
  },
};
